import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import {
  getMyGeneratedSite,
  getSiteOffers,
  markGeneratedSiteInterest,
  markGeneratedSiteViewed,
  subscribeToSite,
  buySiteCredits,
  cancelSiteSubscription,
  markSiteAdInterest,
  requestSiteEdit,
  reserveSiteDomain,
  resendDomainVerificationEmail,
  GeneratedSite,
  SiteOffers,
  SitePriceVariant,
} from '@api/generated-site'
import { useAxiosQuery } from './useAxiosQuery'

export const GENERATED_SITE_QUERY_KEY = ['generated-site', 'mine']
// une entrée de cache par grille de l'A/B test de prix
const siteOffersQueryKey = (variant: SitePriceVariant) => ['generated-site', 'offers', variant]

const IN_PROGRESS_STATUSES = ['PENDING', 'QUOTA_DEFERRED', 'GENERATING']

export const isGeneratedSiteInProgress = (site?: GeneratedSite | null) => !!site && IN_PROGRESS_STATUSES.includes(site.status)

// poll toutes les 3 s tant qu'une génération ou une modification est en cours ; un 404 signifie « pas de site »
export const useMyGeneratedSite = (options: { enabled?: boolean } = {}) => useAxiosQuery<GeneratedSite>({
  queryKey: GENERATED_SITE_QUERY_KEY,
  queryFn: getMyGeneratedSite,
  enabled: options.enabled ?? true,
  retry: (failureCount, error) => {
    if ((error as AxiosError)?.response?.status === 404) return false
    return failureCount < 2
  },
  refetchInterval: (query) => (isGeneratedSiteInProgress(query.state.data?.data) ? 3000 : false),
  refetchOnWindowFocus: false,
  staleTime: 0,
})

export const useSiteOffers = (variant: SitePriceVariant) => useAxiosQuery<SiteOffers>({
  queryKey: siteOffersQueryKey(variant),
  queryFn: () => getSiteOffers(variant),
  staleTime: 10 * 60 * 1000,
})

const useSiteMutation = <TArgs, TData extends { data: unknown }>(
  mutationFn: (args: TArgs) => Promise<TData>,
  pickSite: (data: TData['data']) => GeneratedSite | undefined,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (response) => {
      const site = pickSite(response.data)
      if (site) {
        queryClient.setQueryData(GENERATED_SITE_QUERY_KEY, { ...response, data: site })
      } else {
        queryClient.invalidateQueries({ queryKey: GENERATED_SITE_QUERY_KEY })
      }
    },
  })
}

export const useMarkGeneratedSiteViewed = () => useSiteMutation(markGeneratedSiteViewed, (data) => data as GeneratedSite)

export const useMarkGeneratedSiteInterest = () => useSiteMutation(markGeneratedSiteInterest, (data) => data as GeneratedSite)

export const useSubscribeToSite = () => useSiteMutation(subscribeToSite, (data) => (data as { site: GeneratedSite }).site)

export const useBuySiteCredits = () => useSiteMutation(buySiteCredits, (data) => (data as { site: GeneratedSite }).site)

export const useCancelSiteSubscription = () => useSiteMutation(cancelSiteSubscription, (data) => (data as { site: GeneratedSite }).site)

export const useMarkSiteAdInterest = () => useSiteMutation(markSiteAdInterest, (data) => data as GeneratedSite)

export const useReserveSiteDomain = () => useSiteMutation(reserveSiteDomain, (data) => (data as { site: GeneratedSite }).site)

export const useResendDomainVerificationEmail = () => useMutation({ mutationFn: resendDomainVerificationEmail })

// le serveur répond 202 tout de suite et modifie le site en arrière-plan : l'invalidation relit un site GENERATING
// et le poll ci-dessus prend le relais ; onSettled pour rafraîchir aussi après un refus (crédits, site occupé)
export const useRequestSiteEdit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: requestSiteEdit,
    onSettled: () => queryClient.invalidateQueries({ queryKey: GENERATED_SITE_QUERY_KEY }),
  })
}
